import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ParameterRow } from '../styles';
import Typography from '@mui/material/Typography';
import SelectInput from '@/components/Input/SelectInput';
import TextField from '@/components/Input/TextField';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import type { SelectChangeEvent } from '@mui/material';

interface FieldValueDetail {
  value: string | number | boolean;
  writable: number;
}

interface NetworkInterfaceValues {
  [key: string]: FieldValueDetail | undefined;
}

interface FormValues {
  [key: string]: string | number | boolean | null | Array<string | number>;
}

interface FormFieldRowProps {
  field: EditorPropType;
  fieldValues: NetworkInterfaceValues;
  translatePath: string;
  externalOptions: {
    [key: string]: {
      options?: string[];
    };
  };
}

const FormFieldRow = ({
  field,
  fieldValues,
  translatePath,
  externalOptions,
}: FormFieldRowProps) => {
  const { values, handleChange, setFieldValue, touched, errors } =
    useFormikContext<FormValues>();
  const { t } = useTranslation();

  const fieldKey = String(field.field);

  // visibility buisness rules
  const isVisible = useMemo(() => {
    if (!field.notVisible) return true;
    const controllerValue = values[field.notVisible];
    return controllerValue === true || controllerValue === 'true';
  }, [field.notVisible, values]);

  // centralized disabled logic to avoid duplications in the JSX and to handle the complex logic of disabled fields
  const isDisabled = useMemo(() => {
    if (field.notEditable || fieldValues[fieldKey]?.writable === 0) return true;

    return (
      field.disabledFields?.some((val) => {
        if (typeof val === 'object' && val !== null) {
          return String(values[val.field]) === String(val.value);
        }
        const refVal = values[String(val)];
        return refVal === 'false' || refVal === '' || !refVal;
      }) ?? false
    );
  }, [field, values, fieldValues, fieldKey]);

  // assurance function to get the error message as string to helperText
  // since errors[fieldKey] can be string | string[] | undefined
  const getErrorMessage = (): string => {
    const error = errors[fieldKey];
    const isTouched = touched[fieldKey];

    if (isTouched && typeof error === 'string') {
      return error;
    }
    return '';
  };

  if (!isVisible || values[fieldKey] === null) return null;

  return (
    <ParameterRow>
      <Typography
        sx={{ fontWeight: 600, fontSize: '12px', textAlign: 'right' }}
      >
        {t(`${translatePath}.${fieldKey}`)}
      </Typography>

      {field.type === 'select' && (
        <SelectInput
          options={
            externalOptions[field.field]
              ? externalOptions[field.field].options
              : field.options || []
          }
          value={
            field.multiple
              ? (() => {
                  const val = values[fieldKey];
                  if (Array.isArray(val)) {
                    return val;
                  }
                  if (typeof val === 'string' && val) {
                    return val.split(',').map((v) => v.trim());
                  }
                  return [];
                })()
              : String(values[fieldKey] ?? '')
          }
          name={fieldKey}
          id={fieldKey}
          onChange={(e: SelectChangeEvent) => {
            const val = e.target.value;
            if (field.multiple && Array.isArray(val)) {
              setFieldValue(fieldKey, val.join(','));
            } else {
              setFieldValue(fieldKey, val);
            }
          }}
          disabled={isDisabled}
          multiple={field.multiple}
          error={touched[fieldKey] && Boolean(errors[fieldKey])}
          helperText={getErrorMessage()}
        />
      )}

      {field.type === 'text' && (
        <TextField
          name={fieldKey}
          disabled={isDisabled}
          value={String(values[fieldKey] ?? '')}
          onChange={handleChange}
          sx={{ width: '100%' }}
          error={touched[fieldKey] && Boolean(errors[fieldKey])}
          helperText={getErrorMessage()}
        />
      )}

      {field.type === 'boolean' && (
        <Stack gap="4px">
          <ToggleButtonGroup
            color="primary"
            value={values[fieldKey]}
            sx={{ height: 36 }}
            id={fieldKey}
            onChange={(_, value) => {
              if (value !== null) setFieldValue(fieldKey, value);
            }}
            disabled={isDisabled}
            exclusive
          >
            <ToggleButton value="true">{t('common.activated')}</ToggleButton>
            <ToggleButton value="false">{t('common.disabled')}</ToggleButton>
          </ToggleButtonGroup>
          {getErrorMessage() && (
            <FormHelperText
              error={touched[fieldKey] && Boolean(errors[fieldKey])}
            >
              {getErrorMessage()}
            </FormHelperText>
          )}
        </Stack>
      )}
    </ParameterRow>
  );
};

export default FormFieldRow;
